import { useState, useEffect } from 'react'
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import {
  LayoutDashboard,
  BookOpen,
  GraduationCap,
  Star,
  MessageSquare,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  Search,
  Award
} from 'lucide-react'
import supabase from '../../services/supabaseClient'
import LanguageSelector from '../../components/LanguageSelector'

export default function TeacherDashboard() {
  const { t } = useTranslation()
  const { profile, signOut } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [myClasses, setMyClasses] = useState([])
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalClasses: 0,
    unreadMessages: 0
  })

  useEffect(() => {
    if (profile?.id) {
      fetchTeacherData()
    }
  }, [profile])

  async function fetchTeacherData() {
    try {
      const { data: classes, error: classesError } = await supabase
        .from('classes')
        .select('*')
        .eq('teacher_id', profile.id)

      if (classesError) throw classesError
      setMyClasses(classes || [])

      const classIds = classes?.map(c => c.id) || []
      let totalStudents = 0
      if (classIds.length > 0) {
        const { count } = await supabase
          .from('class_enrollments')
          .select('*', { count: 'exact', head: true })
          .in('class_id', classIds)
        totalStudents = count || 0
      }

      const { count: messagesCount } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', profile.id)
        .eq('is_read', false)

      setStats({
        totalStudents,
        totalClasses: classes?.length || 0,
        unreadMessages: messagesCount || 0
      })
    } catch (error) {
      console.error('Error fetching teacher data:', error)
    }
  }

  const navigation = [
    { name: t('common.dashboard'), href: '/teacher', icon: LayoutDashboard },
    { name: t('common.myClasses'), href: '/teacher/classes', icon: BookOpen },
    { name: t('roles.student') + 's', href: '/teacher/learners', icon: GraduationCap },
    { name: t('dashboard.points'), href: '/teacher/points', icon: Star },
    { name: t('common.messages'), href: '/teacher/messages', icon: MessageSquare },
    { name: t('common.announcements'), href: '/teacher/announcements', icon: Bell },
    { name: t('common.settings'), href: '/teacher/settings', icon: Settings },
  ]

  const isActive = (href) => location.pathname === href

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white border-r border-gray-100">
          <div className="flex h-20 items-center justify-between px-6 border-b border-gray-100">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-xl bg-success-500 flex items-center justify-center text-white font-bold shadow-lg shadow-success-500/30">
                T
              </div>
              <span className="ml-3 text-lg font-bold text-text-primary">{t('roles.teacher')} {t('navigation.portal')}</span>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-text-secondary">
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-primary-500' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={signOut}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-text-secondary rounded-xl hover:bg-gray-100 hover:text-text-primary transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              {t('auth.signOut')}
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r border-gray-100">
          <div className="flex h-20 items-center px-6 border-b border-gray-100">
            <div className="h-10 w-10 rounded-xl bg-success-500 flex items-center justify-center text-white font-bold shadow-lg shadow-success-500/30">
              T
            </div>
            <span className="ml-3 text-lg font-bold text-text-primary">{t('roles.teacher')} {t('navigation.portal')}</span>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
              >
                <item.icon className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-primary-500' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={signOut}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-text-secondary rounded-xl hover:bg-gray-100 hover:text-text-primary transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              {t('auth.signOut')}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top header */}
        <header className="dashboard-header sticky top-0 z-30">
          <div className="flex h-20 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-text-secondary hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            {/* Search */}
            <div className="hidden md:flex flex-1 max-w-md ml-8">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('common.search')}
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white border border-gray-200 text-text-primary placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>
            
            <div className="flex items-center ml-auto space-x-4">
              <LanguageSelector size="small" />
              <Link
                to="/teacher/points/award"
                className="hidden sm:flex items-center px-4 py-2.5 text-sm font-semibold text-success-600 bg-card-green rounded-xl border border-success-200 hover:bg-success-50 transition-colors"
              >
                <Award className="h-4 w-4 mr-1.5" />
                {t('common.award')} {t('dashboard.points')}
              </Link>
              <button className="p-3 rounded-xl text-text-secondary hover:text-text-primary hover:bg-gray-100 transition-colors relative">
                <MessageSquare className="h-5 w-5" />
                {stats.unreadMessages > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-5 w-5 bg-primary-500 rounded-full text-xs text-white flex items-center justify-center border-2 border-white">
                    {stats.unreadMessages}
                  </span>
                )}
              </button>
              <div className="flex items-center pl-4 border-l border-gray-100">
                <div className="h-10 w-10 rounded-xl bg-success-500 flex items-center justify-center text-white font-semibold shadow-lg shadow-success-500/30">
                  {profile?.first_name?.[0]}{profile?.last_name?.[0]}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p className="text-sm font-semibold text-text-primary">{profile?.first_name} {profile?.last_name}</p>
                  <p className="text-xs text-text-secondary">{t('roles.teacher')}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="py-8 px-4 sm:px-6 lg:px-8">
          <Routes>
            <Route
              path="/"
              element={
                <div className="space-y-8 animate-fade-in">
                  {/* Page Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h1 className="text-2xl font-bold text-text-primary font-heading">{t('roles.teacher')} {t('common.dashboard')}</h1>
                      <p className="mt-1 text-sm text-text-secondary">
                        {t('dashboard.welcome', { name: profile?.first_name })}
                      </p>
                    </div>
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon primary">
                          <BookOpen className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.totalClasses')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.totalClasses}</p>
                        </div>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon success">
                          <GraduationCap className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.myStudents')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.totalStudents}</p>
                        </div>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon warning">
                          <MessageSquare className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.unreadMessages')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.unreadMessages}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* My Classes */}
                  <div className="card p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-lg font-bold text-text-primary font-heading">{t('common.myClasses')}</h2>
                      <Link to="/teacher/classes" className="text-sm font-semibold text-primary-600 hover:text-primary-700">
                        {t('dashboard.viewAll')}
                      </Link>
                    </div>
                    {myClasses.length === 0 ? (
                      <div className="text-center py-12 text-text-secondary">
                        <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p className="font-medium">{t('demo.noClasses')}</p>
                        <p className="text-sm mt-1">{t('demo.contactAdmin')}</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {myClasses.map((cls) => (
                          <Link
                            key={cls.id}
                            to={`/teacher/classes/${cls.id}`}
                            className="block p-5 border border-gray-200 rounded-xl hover:border-primary-300 hover:shadow-soft transition-all group"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-text-primary group-hover:text-primary-600 transition-colors">{cls.name}</h3>
                                <p className="text-sm text-text-secondary mt-1">{cls.grade_level}</p>
                              </div>
                              <div className="p-2 bg-card-teal rounded-lg group-hover:bg-primary-100 transition-colors">
                                <BookOpen className="h-5 w-5 text-primary-500" />
                              </div>
                            </div>
                            <div className="mt-4 flex items-center text-sm text-text-secondary">
                              <GraduationCap className="h-4 w-4 mr-2" />
                              <span>{t('common.view')} {t('roles.student')}s</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="card p-6">
                      <h2 className="text-lg font-bold text-text-primary mb-6 font-heading">{t('dashboard.quickActions')}</h2>
                      <div className="grid grid-cols-2 gap-4">
                        <Link
                          to="/teacher/points/award"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-success-300 hover:bg-card-green transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-green text-success-500 group-hover:bg-success-100 transition-colors">
                            <Star className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.award')} {t('dashboard.points')}</span>
                        </Link>
                        <Link
                          to="/teacher/announcements/new"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <Bell className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('announcements.post')}</span>
                        </Link>
                        <Link
                          to="/teacher/messages"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <MessageSquare className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.message')} {t('roles.parent')}s</span>
                        </Link>
                        <Link
                          to="/teacher/learners"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <GraduationCap className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.view')} {t('dashboard.studentProgress')}</span>
                        </Link>
                      </div>
                    </div>

                    <div className="card p-6">
                      <h2 className="text-lg font-bold text-text-primary mb-6 font-heading">{t('dashboard.recentActivity')}</h2>
                      <div className="space-y-4">
                        <div className="flex items-center p-4 rounded-xl bg-gray-50">
                          <div className="w-2 h-2 bg-success-500 rounded-full mr-3" />
                          <span className="text-text-secondary flex-1">{t('demo.pointsAwarded')}</span>
                          <span className="text-sm text-text-secondary">2h {t('common.ago')}</span>
                        </div>
                        <div className="flex items-center p-4 rounded-xl bg-gray-50">
                          <div className="w-2 h-2 bg-primary-500 rounded-full mr-3" />
                          <span className="text-text-secondary flex-1">{t('demo.announcementPosted')}</span>
                          <span className="text-sm text-text-secondary">5h {t('common.ago')}</span>
                        </div>
                        <div className="flex items-center p-4 rounded-xl bg-gray-50">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mr-3" />
                          <span className="text-text-secondary flex-1">{t('demo.messageFromParent')}</span>
                          <span className="text-sm text-text-secondary">1d {t('common.ago')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
            <Route path="*" element={<Navigate to="/teacher" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
